import { TypeaheadResult } from "@mirage/mosaics/SearchBarWithTypeahead/useConvertToTypeaheadResults";
import { isInsideExtensionSidePanel } from "@mirage/shared/util/webExtensionsHelpers";
import { useLayoutEffect, useMemo, useRef } from "react";
import {
  BANNER_HEIGHT,
  BANNER_HEIGHT_EXTENSION_SIDE_PANEL,
  CHIPS_ROW_HEIGHT,
  CHIPS_ROW_HEIGHT_SELECTOR,
  DROPDOWN_CLOSED_HEIGHT_SELECTOR,
  DROPDOWN_OPEN_HEIGHT_SELECTOR,
  MAX_HEIGHT,
  OVERLAY_CLASSNAME_SELECTOR,
} from "../constants";

interface UseResultsContainerAnimationProps {
  isTypeaheadOpen: boolean;
  results: TypeaheadResult[];
  expandMotionVersion?: string;
  isDualModeLauncher?: boolean;
  overlayContainerRef?: React.RefObject<HTMLDivElement>;
}

export function useResultsContainerAnimation({
  isTypeaheadOpen,
  results,
  expandMotionVersion,
  isDualModeLauncher,
  overlayContainerRef,
}: UseResultsContainerAnimationProps) {
  const listContainerRef = useRef<HTMLUListElement>(null);
  const bannerRef = useRef<HTMLDivElement>(null);

  const bannerHeightConst = isInsideExtensionSidePanel()
    ? BANNER_HEIGHT_EXTENSION_SIDE_PANEL
    : BANNER_HEIGHT;

  // We need to track the section-key because
  // we can have the same number of results, but different compositions of sections
  // and sections effect the height of the list.
  // This allows us to listen to rendered-result-list changes that don't effect
  // the length of results, but do effect the section composition.
  const sectionKey = useMemo(
    () =>
      results.reduce(
        (key, { scoredResult: { sectionId } }) =>
          `${key}-${sectionId ?? "undefined"}`,
        "",
      ),
    [results],
  );

  useLayoutEffect(() => {
    if (expandMotionVersion === "OFF" || isDualModeLauncher) return;

    const listContainer = listContainerRef.current;
    const banner = bannerRef.current?.firstChild as HTMLDivElement;

    let overlay: HTMLElement | null = null;

    if (overlayContainerRef?.current) {
      overlay = overlayContainerRef.current.querySelector(
        OVERLAY_CLASSNAME_SELECTOR,
      );
    }

    if (!overlay) {
      overlay = document.querySelector(OVERLAY_CLASSNAME_SELECTOR);
    }

    if (!overlay) return;

    if (isTypeaheadOpen && listContainer) {
      requestAnimationFrame(() => {
        const bannerHeight = banner ? banner.offsetHeight : bannerHeightConst;
        const listHeight = listContainer.scrollHeight + bannerHeight;
        const totalHeight = Math.min(listHeight, MAX_HEIGHT);

        overlay.style.setProperty(
          DROPDOWN_OPEN_HEIGHT_SELECTOR,
          `${totalHeight}px`,
        );
      });
    } else {
      const chipRowHeight =
        getComputedStyle(overlay).getPropertyValue(CHIPS_ROW_HEIGHT_SELECTOR) ||
        `${CHIPS_ROW_HEIGHT}px`;

      overlay.style.setProperty(DROPDOWN_CLOSED_HEIGHT_SELECTOR, chipRowHeight);
    }
  }, [
    isTypeaheadOpen,
    results.length,
    expandMotionVersion,
    isDualModeLauncher,
    bannerHeightConst,
    sectionKey,
  ]);

  return { listContainerRef, bannerRef };
}
